import { FileText, Users } from 'lucide-react';

interface SignerContext {
  role: string;
  order: number;
  totalSigners: number;
}

interface SignerInfoPanelDocument {
  name: string;
  pageCount: number;
}

interface SignerInfoPanelContact {
  firstName: string;
  lastName?: string;
  email?: string;
}

interface SignerInfoPanelProps {
  document: SignerInfoPanelDocument;
  contact: SignerInfoPanelContact;
  signerContext?: SignerContext;
  signedCount: number;
  requiredCount: number;
  totalFields: number;
  /** When true, hides the top document-info card (useful when the drawer header already shows it). */
  hideDocumentCard?: boolean;
}

export function SignerInfoPanel({
  document,
  contact,
  signerContext,
  signedCount,
  requiredCount,
  totalFields,
  hideDocumentCard = false,
}: SignerInfoPanelProps) {
  return (
    <div className="flex flex-col gap-4">
      {!hideDocumentCard && (
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
            <FileText className="h-5 w-5 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="truncate font-medium text-gray-900">
              {document.name}
            </h2>
            <p className="text-sm text-gray-500">
              {document.pageCount} page{document.pageCount > 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}

      <div className="rounded-lg bg-gray-50 p-3">
        <p className="text-sm text-gray-500">Signing as</p>
        <p className="font-medium text-gray-900">
          {contact.firstName} {contact.lastName}
        </p>
        {contact.email && (
          <p className="text-sm text-gray-500">{contact.email}</p>
        )}
      </div>

      {signerContext && (
        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
          <div className="flex items-start gap-2">
            <Users className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-blue-800">
                {signerContext.role}
              </p>
              <p className="text-xs text-blue-600">
                Signer {signerContext.order} of {signerContext.totalSigners}
              </p>
            </div>
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-gray-700">
            Fields to Complete
          </p>
          <span className="text-xs text-gray-500">
            {signedCount} of {requiredCount} required
          </span>
        </div>

        {signedCount === 0 && totalFields > 0 && (
          <div className="mb-3 rounded-lg bg-blue-50 border border-blue-200 p-3">
            <p className="text-sm text-blue-800">
              Complete the fields below to sign this document.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
